import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import { LanguageProvider } from "@/context/LanguageContext";
import AccountTypeSetup from "@/components/AccountTypeSetup";
import LandingPage from "@/pages/LandingPage";
import UserDashboard from "@/pages/UserDashboard";
import CompanyDashboard from "@/pages/CompanyDashboard";
import ProductsServicesPage from "@/pages/ProductsServicesPage";
import ClientsPage from "@/pages/ClientsPage";
import SalesPage from "@/pages/SalesPage";
import ObjectivesPage from "@/pages/ObjectivesPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import InvoicesPage from "@/pages/InvoicesPage";
import InvoiceTemplatesPage from "@/pages/InvoiceTemplatesPage";
import RecurringRevenuePage from "@/pages/RecurringRevenuePage";
import CashFlowPage from "@/pages/CashFlowPage";
import PaymentAgingPage from "@/pages/PaymentAgingPage";
import TaxManagementPage from "@/pages/TaxManagementPage";
import BreakEvenPage from "@/pages/BreakEvenPage";
import InventoryPage from "@/pages/InventoryPage";
import TimeTrackingPage from "@/pages/TimeTrackingPage";
import ProductGovernanceMatrixPage from "@/pages/ProductGovernanceMatrixPage";
import SettingsPage from "@/pages/SettingsPage";
import GoalsPage from "@/pages/GoalsPage";
import AIChat from "@/pages/AIChat";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminLogin from "@/pages/AdminLogin";
import "@/App.css";

const ProtectedRoute = ({ children, adminOnly = false, companyOnly = false, individualOnly = false }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  if (companyOnly && user.user_type !== "company") {
    return <Navigate to="/dashboard" replace />;
  }

  if (individualOnly && user.user_type === "company") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Smart Dashboard Router - routes to appropriate dashboard based on user type
// Also shows account type setup dialog if user hasn't confirmed their account type
const SmartDashboard = () => {
  const { user, updateUser } = useAuth();
  const [showAccountSetup, setShowAccountSetup] = useState(false);
  
  useEffect(() => {
    // Show account type setup if user hasn't confirmed their account type
    // Skip for admin users
    if (user && user.role !== "admin" && !user.account_type_confirmed) {
      setShowAccountSetup(true);
    }
  }, [user]);

  const handleAccountSetupComplete = async () => {
    // Refresh user data from server
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });
      if (response.ok) {
        const userData = await response.json();
        updateUser(userData);
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
    }
    setShowAccountSetup(false);
  };
  
  return (
    <>
      <AccountTypeSetup 
        open={showAccountSetup} 
        onComplete={handleAccountSetupComplete}
      />
      {user?.user_type === "company" ? <CompanyDashboard /> : <UserDashboard />}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <BrandProvider>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/dashboard/*"
                element={
                <ProtectedRoute>
                  <SmartDashboard />
                </ProtectedRoute>
              }
            />
            {/* Company-only routes */}
            <Route
              path="/products-services"
              element={
                <ProtectedRoute companyOnly>
                  <ProductsServicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <ProtectedRoute companyOnly>
                  <ClientsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute companyOnly>
                  <SalesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/objectives"
              element={
                <ProtectedRoute companyOnly>
                  <ObjectivesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute companyOnly>
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute companyOnly>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoice-templates"
              element={
                <ProtectedRoute companyOnly>
                  <InvoiceTemplatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recurring-revenue"
              element={
                <ProtectedRoute companyOnly>
                  <RecurringRevenuePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cash-flow"
              element={
                <ProtectedRoute companyOnly>
                  <CashFlowPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-aging"
              element={
                <ProtectedRoute companyOnly>
                  <PaymentAgingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tax-management"
              element={
                <ProtectedRoute companyOnly>
                  <TaxManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/break-even"
              element={
                <ProtectedRoute companyOnly>
                  <BreakEvenPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute companyOnly>
                  <InventoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/time-tracking"
              element={
                <ProtectedRoute companyOnly>
                  <TimeTrackingPage />
                </ProtectedRoute>
              }
            />
            {/* Individual-only routes */}
            <Route
              path="/goals"
              element={
                <ProtectedRoute individualOnly>
                  <GoalsPage />
                </ProtectedRoute>
              }
            />
            {/* Shared routes */}
            <Route
              path="/product-governance"
              element={
                <ProtectedRoute>
                  <ProductGovernanceMatrixPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-chat"
              element={
                <ProtectedRoute>
                  <AIChat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors closeButton />
          </BrandProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
